/**
 * GroupedSelect
 *
 * Reusable grouped dropdown that mirrors PrimeReact's grouped Dropdown / MultiSelect.
 * Built on top of react-select v5 so it inherits virtualisation, search, portal
 * support, and keyboard navigation out of the box.
 *
 * Accepts data in PrimeReact's grouped format:
 *   [{ label: 'Group A', items: [{ label: 'Opt 1', value: 1 }, …] }, …]
 *
 * The group-label key and children key are configurable via `optionGroupLabel`
 * and `optionGroupChildren` so callers can swap in any shape without mapping.
 *
 * @param {Object}            props
 * @param {Array}             props.options              - Grouped options array
 * @param {*}                 props.value                - Selected value(s)
 * @param {Function}          props.onChange              - Change handler
 * @param {string}            [props.optionGroupLabel]    - Key for the group heading (default "label")
 * @param {string}            [props.optionGroupChildren] - Key for child items array (default "items")
 * @param {string}            [props.optionLabel]         - Key for option display text (default "label")
 * @param {string}            [props.optionValue]         - Key for option value (default "value")
 * @param {string}            [props.placeholder]
 * @param {boolean}           [props.isMulti]
 * @param {boolean}           [props.isClearable]
 * @param {boolean}           [props.isDisabled]
 * @param {boolean}           [props.isSearchable]
 * @param {boolean}           [props.showSelectAll]       - Show "Select All" per group header
 * @param {Function}          [props.itemTemplate]        - Custom renderer for each option
 * @param {Function}          [props.groupTemplate]       - Custom renderer for group headers
 * @param {Function}          [props.optionDisabled]      - Predicate returning true for disabled items
 * @param {string}            [props.display]             - "chip" (default) or "comma"
 * @param {string}            [props.className]
 * @param {Object}            [props.styles]              - react-select style overrides
 * @param {string}            [props.name]
 * @param {string}            [props.id]
 * @param {Function}          [props.onBlur]
 * @param {Function}          [props.onFocus]
 * @param {string}            [props.emptyFilterMessage]
 * @param {string}            [props.menuPlacement]
 */

import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Select, { components } from 'react-select';
import '../shared/AsyncSelect.scss';
import './GroupedSelect.scss';

function GroupedSelect({
  options = [],
  value,
  onChange,
  optionGroupLabel = 'label',
  optionGroupChildren = 'items',
  optionLabel = 'label',
  optionValue = 'value',
  placeholder = 'Select',
  isMulti = false,
  isClearable = true,
  isDisabled = false,
  isSearchable = true,
  showSelectAll = false,
  itemTemplate,
  groupTemplate,
  optionDisabled,
  display = 'chip',
  className = '',
  styles = {},
  name,
  id,
  onBlur,
  onFocus,
  emptyFilterMessage = 'No records found',
  menuPlacement = 'auto',
  ...rest
}) {
  const groupedOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.map((group) => ({
      label: group[optionGroupLabel],
      options: (group[optionGroupChildren] || []).map((item) => ({
        ...item,
        _groupLabel: group[optionGroupLabel],
        label: item[optionLabel],
        value: item[optionValue],
      })),
    }));
  }, [options, optionGroupLabel, optionGroupChildren, optionLabel, optionValue]);

  const allFlatOptions = useMemo(() => groupedOptions.flatMap((g) => g.options), [groupedOptions]);

  const selectedOptions = useMemo(() => {
    if (value == null) return isMulti ? [] : null;
    if (!isMulti) {
      return allFlatOptions.find((o) => o.value === value) ?? null;
    }
    if (!Array.isArray(value)) return [];
    return allFlatOptions.filter((o) => value.includes(o.value));
  }, [value, isMulti, allFlatOptions]);

  const handleChange = useCallback(
    (selected) => {
      if (!isMulti) {
        onChange(selected ? selected.value : null);
        return;
      }
      const values = selected ? selected.map((s) => s.value) : [];
      onChange(values);
    },
    [isMulti, onChange]
  );

  const handleGroupSelect = useCallback(
    (groupLabel) => {
      if (!isMulti) return;
      const group = groupedOptions.find((g) => g.label === groupLabel);
      if (!group) return;

      const isItemDisabled = optionDisabled || (() => false);
      const enabledInGroup = group.options.filter((o) => !isItemDisabled(o));
      const currentValues = Array.isArray(value) ? value : [];
      const allSelected = enabledInGroup.every((o) => currentValues.includes(o.value));

      let next;
      if (allSelected) {
        const groupValues = new Set(enabledInGroup.map((o) => o.value));
        next = currentValues.filter((v) => !groupValues.has(v));
      } else {
        const existing = new Set(currentValues);
        enabledInGroup.forEach((o) => existing.add(o.value));
        next = Array.from(existing);
      }
      onChange(next);
    },
    [isMulti, value, groupedOptions, onChange, optionDisabled]
  );

  const GroupHeading = useCallback(
    (props) => {
      const { children, data } = props;
      const currentValues = Array.isArray(value) ? value : [];
      const isItemDisabled = optionDisabled || (() => false);
      const enabledInGroup = (data?.options || []).filter((o) => !isItemDisabled(o));
      const allChecked = enabledInGroup.length > 0 && enabledInGroup.every((o) => currentValues.includes(o.value));
      const someChecked = !allChecked && enabledInGroup.some((o) => currentValues.includes(o.value));

      return (
        <div
          className="grouped-select__group-heading"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (showSelectAll && isMulti) handleGroupSelect(data.label);
          }}
        >
          {showSelectAll && isMulti && (
            <span
              className={[
                'grouped-select__group-checkbox',
                allChecked ? 'grouped-select__group-checkbox--checked' : '',
                someChecked ? 'grouped-select__group-checkbox--partial' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-hidden="true"
            />
          )}
          <span className="grouped-select__group-label">{groupTemplate ? groupTemplate(data) : children}</span>
        </div>
      );
    },
    [value, showSelectAll, isMulti, handleGroupSelect, groupTemplate, optionDisabled]
  );

  const CustomOption = useCallback(
    (props) => {
      const { innerRef, innerProps, isSelected, isFocused, isDisabled: optDisabled, data: optData } = props;
      return (
        <div
          ref={innerRef}
          {...innerProps}
          className={[
            'grouped-select__option',
            isFocused ? 'grouped-select__option--focused' : '',
            isSelected ? 'grouped-select__option--selected' : '',
            optDisabled ? 'grouped-select__option--disabled' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {isMulti && (
            <span className={`grouped-select__checkbox${isSelected ? ' grouped-select__checkbox--checked' : ''}`} aria-hidden="true" />
          )}
          <span className="grouped-select__option-label">{itemTemplate ? itemTemplate(optData) : optData.label}</span>
        </div>
      );
    },
    [isMulti, itemTemplate]
  );

  const MultiValueDisplay = useCallback(
    (props) => {
      if (display === 'comma') {
        const allSelected = Array.isArray(selectedOptions) ? selectedOptions : [];
        const idx = allSelected.findIndex((o) => o.value === props.data.value);
        if (idx > 0) return null;
        return <div className="grouped-select__comma-value">{allSelected.map((o) => o.label).join(', ')}</div>;
      }
      return <components.MultiValue {...props} />;
    },
    [display, selectedOptions]
  );

  const customComponents = useMemo(
    () => ({
      Option: CustomOption,
      GroupHeading,
      MultiValue: MultiValueDisplay,
    }),
    [CustomOption, GroupHeading, MultiValueDisplay]
  );

  const mergedStyles = useMemo(
    () => ({
      control: (base) => ({
        ...base,
        minHeight: '38px',
        flexWrap: 'wrap',
      }),
      valueContainer: (base) => ({
        ...base,
        flexWrap: 'wrap',
        gap: '2px',
      }),
      group: (base) => ({
        ...base,
        paddingTop: 0,
        paddingBottom: 0,
      }),
      groupHeading: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        textTransform: 'none',
        fontSize: '14px',
      }),
      option: (base) => ({
        ...base,
        padding: '6px 12px',
      }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      ...styles,
    }),
    [styles]
  );

  return (
    <Select
      options={groupedOptions}
      value={selectedOptions}
      onChange={handleChange}
      placeholder={placeholder}
      isMulti={isMulti}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable={isSearchable}
      hideSelectedOptions={false}
      closeMenuOnSelect={!isMulti}
      className={`grouped-select-dropdown ${className}`.trim()}
      classNamePrefix="async-select-custom"
      styles={mergedStyles}
      components={customComponents}
      noOptionsMessage={() => emptyFilterMessage}
      menuShouldScrollIntoView={false}
      isOptionDisabled={optionDisabled ? (opt) => optionDisabled(opt) : undefined}
      closeMenuOnScroll={(e) => e.target === document}
      menuPlacement={menuPlacement}
      name={name}
      id={id}
      onBlur={onBlur}
      onFocus={onFocus}
      {...rest}
    />
  );
}

GroupedSelect.propTypes = {
  options: PropTypes.array,
  value: PropTypes.oneOfType([PropTypes.any, PropTypes.array]),
  onChange: PropTypes.func.isRequired,
  optionGroupLabel: PropTypes.string,
  optionGroupChildren: PropTypes.string,
  optionLabel: PropTypes.string,
  optionValue: PropTypes.string,
  placeholder: PropTypes.string,
  isMulti: PropTypes.bool,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isSearchable: PropTypes.bool,
  showSelectAll: PropTypes.bool,
  itemTemplate: PropTypes.func,
  groupTemplate: PropTypes.func,
  optionDisabled: PropTypes.func,
  display: PropTypes.oneOf(['chip', 'comma']),
  className: PropTypes.string,
  styles: PropTypes.object,
  name: PropTypes.string,
  id: PropTypes.string,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  emptyFilterMessage: PropTypes.string,
  menuPlacement: PropTypes.string,
};

export default GroupedSelect;
